import React from "react";

const DiagnosticResultView = ({ result, onBackToForm }) => {
  console.log('🔍 DiagnosticResultView received result:', result);
  if (!result || !result.diagnostic_result) {
    console.log('❌ No result or diagnostic_result found');
    return null;
  }

  const { diagnostic_result } = result;
  console.log('🔍 Diagnostic result structure:', diagnostic_result);

  const urgencyColors = {
    low: "text-green-600 bg-green-50 border-green-200",
    medium: "text-yellow-600 bg-yellow-50 border-yellow-200",
    high: "text-orange-600 bg-orange-50 border-orange-200",
    critical: "text-red-600 bg-red-50 border-red-200",
  };

  const urgencyColor =
    urgencyColors[diagnostic_result.urgency?.toLowerCase()] ||
    urgencyColors.medium;

  const safeToDriveColors = {
    yes: "text-green-600 bg-green-50 border-green-200",
    no: "text-red-600 bg-red-50 border-red-200",
    maybe: "text-yellow-600 bg-yellow-50 border-yellow-200",
  };

  const safeToDriveColor =
    safeToDriveColors[diagnostic_result.safe_to_drive?.toLowerCase()] ||
    safeToDriveColors.maybe;

  return (
    <div className="min-h-screen bg-orange-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-orange-900 mb-2">
                🔧 Automotive Diagnostic Report
              </h1>
              <p className="text-orange-700">
                Analysis completed for your vehicle
              </p>
            </div>
            <button
              onClick={onBackToForm}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
            >
              ← New Analysis
            </button>
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
            🚗 Vehicle Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Make & Model</h3>
              <p className="text-orange-700">
                {diagnostic_result.vehicle?.make} {diagnostic_result.vehicle?.model}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Year</h3>
              <p className="text-orange-700">{diagnostic_result.vehicle?.year}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">VIN</h3>
              <div className="flex items-center gap-2">
                <p className="text-orange-700 font-mono text-sm">
                  {diagnostic_result.vehicle?.vin || "Not available"}
                </p>
                {diagnostic_result.vehicle?.vin && (
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(diagnostic_result.vehicle.vin);
                        // Show a temporary success message
                        const button = event.target;
                        const originalText = button.textContent;
                        button.textContent = "✅ Copied!";
                        button.className = "px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full transition-colors duration-200 flex items-center gap-1";
                        setTimeout(() => {
                          button.textContent = originalText;
                          button.className = "px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200 transition-colors duration-200 flex items-center gap-1";
                        }, 2000);
                      } catch (err) {
                        console.error('Failed to copy VIN:', err);
                      }
                    }}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200 transition-colors duration-200 flex items-center gap-1"
                    title="Copy VIN to clipboard"
                  >
                    📋 Copy
                  </button>
                )}
              </div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Mileage</h3>
              <p className="text-orange-700">
                {diagnostic_result.vehicle?.mileage || "Not available"}
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Vehicle Age</h3>
              <p className="text-orange-700">
                {diagnostic_result.vehicle?.vehicle_age_years || "Not available"} years
              </p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <h3 className="font-semibold text-orange-800 mb-2">Country of Origin</h3>
              <p className="text-orange-700">
                {diagnostic_result.vehicle?.country_of_origin || "Not available"}
              </p>
            </div>
          </div>
        </div>

        {/* Dashboard Findings */}
        {diagnostic_result.dashboard_findings && diagnostic_result.dashboard_findings.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              ⚠️ Dashboard Findings
            </h2>
            <div className="space-y-3">
              {diagnostic_result.dashboard_findings.map((finding, index) => (
                <div
                  key={index}
                  className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                >
                  <p className="text-yellow-800">{finding}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Symptoms Summary */}
        {diagnostic_result.symptoms_summary && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              📝 Symptoms Summary
            </h2>
            <p className="text-orange-700 bg-orange-50 rounded-lg p-4">
              {diagnostic_result.symptoms_summary}
            </p>
          </div>
        )}

        {/* Diagnosis */}
        {diagnostic_result.diagnosis && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              🔍 Diagnosis
            </h2>

            {/* Immediate Concerns */}
            {diagnostic_result.diagnosis.immediate_concerns && diagnostic_result.diagnosis.immediate_concerns.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Immediate Concerns</h3>
                <div className="space-y-2">
                  {diagnostic_result.diagnosis.immediate_concerns.map((concern, index) => (
                    <div
                      key={index}
                      className="bg-red-50 border border-red-200 rounded-lg p-4"
                    >
                      <p className="text-red-800">{concern}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* General Possible Causes */}
            {diagnostic_result.diagnosis.general_possible_causes && diagnostic_result.diagnosis.general_possible_causes.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Possible Causes</h3>
                <div className="space-y-2">
                  {diagnostic_result.diagnosis.general_possible_causes.map((cause, index) => (
                    <div
                      key={index}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                    >
                      <p className="text-blue-800">{cause}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Known Model Issues */}
            {diagnostic_result.diagnosis.known_model_issues && diagnostic_result.diagnosis.known_model_issues.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Known Model Issues</h3>
                <div className="space-y-4">
                  {diagnostic_result.diagnosis.known_model_issues.map((issue, index) => (
                    <div
                      key={index}
                      className="bg-purple-50 border border-purple-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-purple-800">{issue.issue}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          issue.likelihood === 'high' ? 'bg-red-100 text-red-800' :
                          issue.likelihood === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {issue.likelihood} probability
                        </span>
                      </div>
                      <p className="text-purple-700 mb-2">{issue.description}</p>
                      <div className="text-sm text-purple-600">
                        <p><strong>Affected Years:</strong> {issue.affected_years}</p>
                        <p><strong>Symptoms:</strong> {issue.symptoms}</p>
                        {issue.recall_tsb && (
                          <p><strong>Recall/TSB:</strong> {issue.recall_tsb}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Age Related Issues */}
            {diagnostic_result.diagnosis.age_related_issues && diagnostic_result.diagnosis.age_related_issues.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Age Related Issues</h3>
                <div className="space-y-2">
                  {diagnostic_result.diagnosis.age_related_issues.map((issue, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                    >
                      <p className="text-gray-800">{issue}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Urgency and Safety */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Urgency Level */}
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6">
            <h2 className="text-xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              🚨 Urgency Level
            </h2>
            <div className={`rounded-lg p-4 border-2 ${urgencyColor}`}>
              <p className="font-semibold text-lg capitalize">
                {diagnostic_result.urgency || "Unknown"}
              </p>
              {diagnostic_result.urgency_explanation && (
                <p className="mt-2 text-sm">
                  {diagnostic_result.urgency_explanation}
                </p>
              )}
            </div>
          </div>

          {/* Safe to Drive */}
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6">
            <h2 className="text-xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              🚗 Safe to Drive
            </h2>
            <div className={`rounded-lg p-4 border-2 ${safeToDriveColor}`}>
              <p className="font-semibold text-lg capitalize">
                {diagnostic_result.safe_to_drive || "Unknown"}
              </p>
              {diagnostic_result.safe_to_drive_details && (
                <p className="mt-2 text-sm">
                  {diagnostic_result.safe_to_drive_details}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recommended Actions */}
        {diagnostic_result.recommended_action && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              🛠️ Recommended Actions
            </h2>

            {/* Immediate Steps */}
            {diagnostic_result.recommended_action.immediate_steps && diagnostic_result.recommended_action.immediate_steps.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Immediate Steps</h3>
                <div className="space-y-2">
                  {diagnostic_result.recommended_action.immediate_steps.map((step, index) => (
                    <div
                      key={index}
                      className="bg-red-50 border border-red-200 rounded-lg p-4"
                    >
                      <p className="text-red-800">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Short Term */}
            {diagnostic_result.recommended_action.short_term && diagnostic_result.recommended_action.short_term.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Short Term</h3>
                <div className="space-y-2">
                  {diagnostic_result.recommended_action.short_term.map((step, index) => (
                    <div
                      key={index}
                      className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                    >
                      <p className="text-yellow-800">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Long Term */}
            {diagnostic_result.recommended_action.long_term && diagnostic_result.recommended_action.long_term.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-orange-800 mb-3">Long Term</h3>
                <div className="space-y-2">
                  {diagnostic_result.recommended_action.long_term.map((step, index) => (
                    <div
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-lg p-4"
                    >
                      <p className="text-green-800">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DIY vs Professional */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">DIY Possible</h4>
                <p className="text-blue-700 capitalize">
                  {diagnostic_result.recommended_action.diy_possible || "Unknown"}
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">Professional Required</h4>
                <p className="text-purple-700">
                  {diagnostic_result.recommended_action.professional_required || "Unknown"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Context */}
        {diagnostic_result.additional_context && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              📋 Additional Context
            </h2>

            {diagnostic_result.additional_context.common_failure_points && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-orange-800 mb-2">Common Failure Points</h3>
                <p className="text-orange-700 bg-orange-50 rounded-lg p-4">
                  {diagnostic_result.additional_context.common_failure_points}
                </p>
              </div>
            )}

            {diagnostic_result.additional_context.maintenance_history_questions && diagnostic_result.additional_context.maintenance_history_questions.length > 0 && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-orange-800 mb-2">Maintenance History Questions</h3>
                <div className="space-y-2">
                  {diagnostic_result.additional_context.maintenance_history_questions.map((question, index) => (
                    <div
                      key={index}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                    >
                      <p className="text-blue-800">{question}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {diagnostic_result.additional_context.preventive_advice && (
              <div>
                <h3 className="text-lg font-semibold text-orange-800 mb-2">Preventive Advice</h3>
                <p className="text-orange-700 bg-orange-50 rounded-lg p-4">
                  {diagnostic_result.additional_context.preventive_advice}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Confidence Level */}
        {diagnostic_result.confidence_level && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6 mb-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              📊 Confidence Level
            </h2>
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-lg font-semibold ${
                diagnostic_result.confidence_level === 'high' ? 'bg-green-100 text-green-800' :
                diagnostic_result.confidence_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {diagnostic_result.confidence_level.toUpperCase()}
              </div>
              <p className="text-orange-700">
                AI confidence in this diagnosis
              </p>
            </div>
          </div>
        )}

        {/* Additional Notes */}
        {diagnostic_result.additional_notes && (
          <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-6">
            <h2 className="text-2xl font-bold text-orange-900 mb-4 flex items-center gap-2">
              📝 Additional Notes
            </h2>
            <p className="text-orange-700 bg-orange-50 rounded-lg p-4">
              {diagnostic_result.additional_notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticResultView;
